import React from 'react';
import { Box, Breadcrumbs, Paper, Typography } from '@mui/material';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import {
  ClientDetailsV2AddMealPlanDialog,
  type GenericPlanCalorieTargetForm,
} from '@/clients/details/v2/components/ClientDetailsV2AddMealPlanDialog';
import { ClientDetailsV2MealPlanTab } from '@/clients/details/v2/components/ClientDetailsV2MealPlanTab';
import NotificationCenter from '@/components/notifications/NotificationCenter';
import { createGenericMealPlan, getGenericClinicalTags } from '@/lib/apiService';
import { extractErrorMessage, showSuccessToast } from '@/utils/toast-utils';

const DEFAULT_GENERIC_CALORIE_TARGET: GenericPlanCalorieTargetForm = {
  mode: 'direct',
  dailyCalories: '1900',
  ageYears: '',
  gender: '',
  weightKg: '',
  heightCm: '',
  activityLevel: 'moderately_active',
  weightGoal: 'maintain',
};

function getCreatedGenericPlanId(response: unknown): string {
  const payload = response && typeof response === 'object' ? (response as Record<string, unknown>) : {};
  const data = payload.data && typeof payload.data === 'object'
    ? (payload.data as Record<string, unknown>)
    : {};

  return String(
    data.genericPlanId ??
      data.mealPlanId ??
      data.planId ??
      data.id ??
      payload.genericPlanId ??
      payload.mealPlanId ??
      payload.planId ??
      payload.id ??
      ''
  ).trim();
}

const MealPlanningOptionsPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const clientId = searchParams.get('clientId') || '';
  const [isGenericPlanDialogOpen, setIsGenericPlanDialogOpen] = React.useState(false);
  const [genericPlanName, setGenericPlanName] = React.useState('');
  const [genericCalorieTarget, setGenericCalorieTarget] = React.useState<GenericPlanCalorieTargetForm>(
    DEFAULT_GENERIC_CALORIE_TARGET,
  );
  const [genericClinicalTags, setGenericClinicalTags] = React.useState<Array<{ value: string; label: string }>>([]);
  const [selectedGenericClinicalTags, setSelectedGenericClinicalTags] = React.useState<string[]>([]);
  const [isGenericClinicalTagsLoading, setIsGenericClinicalTagsLoading] = React.useState(false);
  const [hasLoadedGenericClinicalTags, setHasLoadedGenericClinicalTags] = React.useState(false);
  const [isCreatingGenericPlan, setIsCreatingGenericPlan] = React.useState(false);
  const [genericPlanFieldErrors, setGenericPlanFieldErrors] = React.useState<{
    mealName?: string;
    client?: string;
    clinicalTags?: string;
    calorieTarget?: string;
    ageYears?: string;
    gender?: string;
    weightKg?: string;
    heightCm?: string;
    activityLevel?: string;
  }>({});
  const [genericPlanFormFeedback, setGenericPlanFormFeedback] = React.useState<{
    variant: 'error' | 'warning' | 'success' | 'info';
    title?: string;
    message: string;
  } | null>(null);
  const openGenericPlanDialog = React.useCallback(() => {
    setGenericPlanName('');
    setSelectedGenericClinicalTags([]);
    setGenericCalorieTarget(DEFAULT_GENERIC_CALORIE_TARGET);
    setGenericPlanFieldErrors({});
    setGenericPlanFormFeedback(null);
    setIsGenericPlanDialogOpen(true);
  }, []);

  const createParam = searchParams.get('create')?.trim().toLowerCase() ?? '';

  React.useEffect(() => {
    if (createParam !== 'generic') return;
    openGenericPlanDialog();
  }, [createParam, openGenericPlanDialog]);

  const loadGenericClinicalTags = React.useCallback(async () => {
    if (isGenericClinicalTagsLoading || hasLoadedGenericClinicalTags) return;

    setIsGenericClinicalTagsLoading(true);
    try {
      const response = await getGenericClinicalTags();
      if (!response.success) {
        throw new Error(response.message || 'Unable to load clinical tags');
      }

      const tags = (response.data ?? [])
        .map((tag) => {
          const value = String(tag.value ?? tag.key ?? tag.id ?? tag.name ?? tag.label ?? '').trim();
          const label = String(tag.label ?? tag.name ?? tag.value ?? tag.key ?? tag.id ?? '').trim();
          return value ? { value, label: label || value } : null;
        })
        .filter((tag): tag is { value: string; label: string } => tag !== null);

      setGenericClinicalTags(tags);
      setHasLoadedGenericClinicalTags(true);
    } catch (error) {
      setGenericClinicalTags([]);
      setHasLoadedGenericClinicalTags(true);
      setGenericPlanFormFeedback({
        variant: 'error',
        title: 'Unable to load clinical tags',
        message: extractErrorMessage(error),
      });
    } finally {
      setIsGenericClinicalTagsLoading(false);
    }
  }, [hasLoadedGenericClinicalTags, isGenericClinicalTagsLoading]);

  const handleCreateGenericPlan = async () => {
    const planName = genericPlanName.trim();
    const nextFieldErrors: typeof genericPlanFieldErrors = {};
    if (!planName) {
      nextFieldErrors.mealName = 'Please enter a meal plan name.';
    }
    if (!selectedGenericClinicalTags.length) {
      nextFieldErrors.clinicalTags = 'Please select at least one clinical tag.';
    }
    if (genericCalorieTarget.mode === 'direct') {
      const dailyCalories = Number(genericCalorieTarget.dailyCalories);
      if (!Number.isFinite(dailyCalories) || dailyCalories < 500 || dailyCalories > 6000) {
        nextFieldErrors.calorieTarget = 'Enter a daily target between 500 and 6,000 kcal.';
      }
    } else {
      const ageYears = Number(genericCalorieTarget.ageYears);
      const weightKg = Number(genericCalorieTarget.weightKg);
      const heightCm = Number(genericCalorieTarget.heightCm);
      if (!Number.isInteger(ageYears) || ageYears < 1 || ageYears > 120) {
        nextFieldErrors.ageYears = 'Enter an age from 1 to 120.';
      }
      if (!genericCalorieTarget.gender) {
        nextFieldErrors.gender = 'Select the sex used by the EER formula.';
      }
      if (!Number.isFinite(weightKg) || weightKg < 1 || weightKg > 500) {
        nextFieldErrors.weightKg = 'Enter a weight from 1 to 500 kg.';
      }
      if (!Number.isFinite(heightCm) || heightCm < 30 || heightCm > 250) {
        nextFieldErrors.heightCm = 'Enter a height from 30 to 250 cm.';
      }
      if (!genericCalorieTarget.activityLevel) {
        nextFieldErrors.activityLevel = 'Select an activity level.';
      }
    }

    if (Object.keys(nextFieldErrors).length > 0) {
      setGenericPlanFieldErrors(nextFieldErrors);
      setGenericPlanFormFeedback(null);
      return;
    }

    setGenericPlanFieldErrors({});
    setGenericPlanFormFeedback(null);
    setIsCreatingGenericPlan(true);
    try {
      const response = await createGenericMealPlan({
        name: planName,
        description: '',
        clinicalTags: selectedGenericClinicalTags,
        calorieTarget: genericCalorieTarget.mode === 'direct'
          ? {
            mode: 'direct',
            dailyCalories: Number(genericCalorieTarget.dailyCalories),
          }
          : {
            mode: 'reference_person',
            referencePerson: {
              ageYears: Number(genericCalorieTarget.ageYears),
              gender: genericCalorieTarget.gender as 'male' | 'female',
              weightKg: Number(genericCalorieTarget.weightKg),
              heightCm: Number(genericCalorieTarget.heightCm),
              activityLevel: genericCalorieTarget.activityLevel,
              bmrFormulaId: 'iom',
              weightGoal: genericCalorieTarget.weightGoal,
            },
          },
        mealSlots: [],
      });

      if (!response.success) {
        throw new Error(response.message || 'Unable to create cohort meal plan');
      }

      setIsGenericPlanDialogOpen(false);
      showSuccessToast('Cohort meal plan created');

      const createdPlanId = getCreatedGenericPlanId(response);
      if (createdPlanId) {
        navigate(`/meal-planning/generic/${encodeURIComponent(createdPlanId)}/nutrition/1`);
      }
    } catch (error) {
      setGenericPlanFormFeedback({
        variant: 'error',
        title: 'Unable to create cohort meal plan',
        message: extractErrorMessage(error),
      });
    } finally {
      setIsCreatingGenericPlan(false);
    }
  };

  return (
    <Box data-demo-id="meal-planning-page" sx={{ position: 'relative', py: 1, mt: { lg: 1.5 }, ml: { lg: 0.5 } }}>
      <Paper
        elevation={0}
        sx={{
          minHeight: 'calc(100vh - 7.5rem)',
          overflow: 'hidden',
          borderRadius: '28px',
          border: '1px solid rgba(15, 23, 42, 0.08)',
          bgcolor: '#fff',
        }}
      >
        <div className="space-y-8 p-4 sm:p-5 lg:px-7 lg:pt-6 lg:pb-7">
          <Paper
            elevation={0}
            sx={{
              borderRadius: '16px',
              border: '1px solid #dbe6eb',
              bgcolor: '#fafcfe',
              px: { xs: 2, sm: 3 },
              py: 1.5,
            }}
          >
            <Breadcrumbs separator="/" aria-label="breadcrumb">
              <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: '14px', color: '#0f172a' }}>
                Meal Planning
              </Typography>
            </Breadcrumbs>
          </Paper>

          <Box
            sx={{
              display: 'flex',
              alignItems: { xs: 'flex-start', sm: 'center' },
              justifyContent: 'space-between',
              gap: 2,
              flexDirection: { xs: 'column', sm: 'row' },
            }}
          >
            <Box sx={{ minWidth: 0 }}>
              <Typography
                sx={{
                  fontFamily: 'Inter, sans-serif',
                  fontSize: '32px',
                  fontWeight: 500,
                  lineHeight: 1.2,
                  color: '#1D2B2D',
                  textTransform: 'capitalize',
                }}
                component="h1"
              >
                Meal Planning
              </Typography>
              <Typography
                sx={{
                  mt: '2px',
                  fontFamily: 'Inter, sans-serif',
                  fontSize: '16px',
                  fontWeight: 400,
                  lineHeight: '20px',
                  color: '#717182',
                }}
              >
                Create and manage meal plans
              </Typography>
            </Box>
            <NotificationCenter buttonVariant="feature" />
          </Box>

          <ClientDetailsV2MealPlanTab clientId={clientId} />
        </div>
      </Paper>

      <ClientDetailsV2AddMealPlanDialog
        open={isGenericPlanDialogOpen}
        onClose={() => setIsGenericPlanDialogOpen(false)}
        showClientSelection={false}
        newMealName={genericPlanName}
        onNewMealNameChange={(value) => {
          setGenericPlanName(value);
          setGenericPlanFieldErrors((current) => ({ ...current, mealName: undefined }));
          setGenericPlanFormFeedback(null);
        }}
        newMealDays={1}
        onNewMealDaysChange={() => {}}
        genericClinicalTagOptions={genericClinicalTags}
        selectedGenericClinicalTags={selectedGenericClinicalTags}
        onGenericClinicalTagsChange={(value) => {
          setSelectedGenericClinicalTags(value);
          setGenericPlanFieldErrors((current) => ({ ...current, clinicalTags: undefined }));
          setGenericPlanFormFeedback(null);
        }}
        onGenericClinicalTagsOpen={loadGenericClinicalTags}
        isGenericClinicalTagsLoading={isGenericClinicalTagsLoading}
        genericCalorieTarget={genericCalorieTarget}
        onGenericCalorieTargetChange={(value) => {
          setGenericCalorieTarget(value);
          setGenericPlanFieldErrors((current) => ({
            ...current,
            calorieTarget: undefined,
            ageYears: undefined,
            gender: undefined,
            weightKg: undefined,
            heightCm: undefined,
            activityLevel: undefined,
          }));
          setGenericPlanFormFeedback(null);
        }}
        selectedPlanType="generic"
        onSelectedPlanTypeChange={() => {}}
        onSubmit={() => {
          void handleCreateGenericPlan();
        }}
        isSubmitting={isCreatingGenericPlan}
        title="Create a cohort meal plan"
        subtitle="Choose clinical tags and define the cohort calorie target"
        mealNameLabel="Meal Plan Name"
        showDaysField={false}
        showPlanTypeOptions={false}
        paperMaxWidth={680}
        compactTypography
        fieldErrors={genericPlanFieldErrors}
        formFeedback={genericPlanFormFeedback}
      />
    </Box>
  );
};

export default MealPlanningOptionsPage;
